import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  selectCompanyCompetitors,
  selectCompanyCompetitorsExecutives,
  selectLoading,
} from 'Selector/TopEntitiesSelector';

import {
  selectCompetitorCall,
  selectCompetitorsFlurry,
} from 'Selector/CompaniesSelector';
import { useDispatch, useSelector } from 'react-redux';
import { Company } from 'Types/CompaniesTypes';
import { Executive } from 'Types/PersonTypes';
import PeopleCardList from 'Components/common/components/PeopleCardList';
import CompanyCardLarge from 'Components/common/components/CompanyCardLarge';
import CompanySkeleton from 'Components/common/skeleton/CompanySkeleton';
import PeopleCardListSkeleton from 'Components/common/skeleton/PeopleCardListSkeleton';
import { Col, Row } from 'antd';
import { PeopleNotFound } from 'Pages/people/PeopleGroupDetail';
import ArrowRightIcon from 'Components/common/assets/svgIcons/ArrowRightIcon';
import { actions as flurryActions } from 'Slice/HelperSlice';
import { backgroundColorParsing, valueToObject } from 'Utils/UtilityFunctions';
import { actions } from 'Slice/CompaniesSlice';
import DragWrapper from 'Components/people/components/DragWrapper';
import {
  primaryHeadingColor, wLight, secondaryFont,
  wMedium, primaryColor, maxWidth, retina, maxMobile,
} from '../../../Utils/utilsStyle';

const Wrapper = styled.div`
h2{
  color:${primaryHeadingColor};
  font-size: 28px;
  font-weight:${wLight};
  font-family:${secondaryFont};
  margin-bottom: 25px;
  @media all and (max-width: ${maxMobile}) {
    font-size: 20px;
    margin-bottom: 10px;
  }
}
 .mt-90{
   margin-top:60px;
 }
 .mb-90{
   margin-bottom: 90px;
 }

 .heading-text {
   display:flex;
   justify-content:space-between;
 }
`;

export const ReadMore = styled.span`
  font-size: 14px;
  font-weight: ${wMedium};
  font-family: ${secondaryFont};
  line-height: 36px;
  letter-spacing: -0.35px;
  color: ${primaryColor};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: flex-end;
   svg {
    margin-left: 10px;
      @media all and (max-width: ${maxWidth}) , ${retina} {
        margin-left: 5px;
        width: 12px;
        height: 12px;
      }
  }
`;

export interface Props {
  isDrag: (d: boolean) => void,
}

const Competitors = function Competitors(props: Props) {
  const { isDrag } = props;
  const dispatch = useDispatch();
  const loading: boolean = useSelector(selectLoading);
  const executives: Executive[] | undefined = useSelector(selectCompanyCompetitorsExecutives);
  const companies: Company[] | undefined = useSelector(selectCompanyCompetitors);
  const flurryObject = useSelector(selectCompetitorsFlurry);
  const [seeAllCompetitors, setSeeAllCompetitors] = useState(true);
  const [seeAllExecutives, setSeeAllExecutives] = useState(true);
  const isCalled = useSelector(selectCompetitorCall);

  useEffect(() => {
    if (flurryObject) {
      dispatch(flurryActions.callFlurryEvent(
        valueToObject('company_executives', 0, {
          ...flurryObject,
          type: 'Executives',
          ...isCalled && { isCalled },
        }),
      ));
      dispatch(flurryActions.callFlurryEvent(
        valueToObject('company_competitors', 0, {
          ...flurryObject,
          ...isCalled && { isCalled },
        }),
      ));
      dispatch(actions.setCompetitorsCall(true));
    }
  }, [flurryObject]);

  const getCompaniesSkeleton = () => {
    const skeletons = [];
    for (let i = 0; i < 12; i += 1) {
      skeletons.push(
        <CompanySkeleton />,
      );
    }
    return skeletons;
  };

  const getExecutivesSkeleton = () => {
    const skeletons = [];
    for (let i = 0; i < 10; i += 1) {
      skeletons.push(
        <Col span={8} xs={24} sm={24} md={12} lg={8}>
          <PeopleCardListSkeleton />
        </Col>,
      );
    }
    return skeletons;
  };

  const getExecutives = () => {
    if (executives?.length) {
      return executives
        .slice(0, !seeAllExecutives && executives.length > 6 ? executives.length : 6)
        .map((item) => (
          <Col span={8} xs={24} sm={24} md={12} lg={8}>
            <DragWrapper key={item.personId} isDrag={isDrag}>
              <PeopleCardList
                executive={item}
                key={item.personId}
                disableNavigation={false}
                openInNewTab
                personName={item.personName}
                logo={item.personImage}
                persona={item.archeType.color}
                tag={item.personDesignation}
                personAddedFlag={item.personAdded}
                personId={item.personId}
                executiveType={item.executiveType}
                companyName={item.companyName}
                personalityTypesColors={item.personalityTypes}
                wrapperColor={
                  backgroundColorParsing(item.personalityTypes)
                }
                showDrawerIcon={!!item.personNotes}
              />
            </DragWrapper>
          </Col>
        ));
    }
    return <PeopleNotFound>No Results found</PeopleNotFound>;
  };

  const seeMoreLess = () => {
    setSeeAllCompetitors(!seeAllCompetitors);
  };

  const seeMoreLess2 = () => {
    setSeeAllExecutives(!seeAllExecutives);
  };

  return (
    <Wrapper>
      {!loading
        ? (
          <div className="mt-50 heading-text">
            <h2>
              Competitors
            </h2>
            {companies && companies.length > 12
              ? (
                <ReadMore
                  onClick={seeMoreLess}
                  onKeyPress={seeMoreLess}
                  role="button"
                  tabIndex={0}
                  aria-label={seeAllCompetitors ? 'See More ' : 'See Less '}
                >
                  {seeAllCompetitors ? 'See More ' : 'See Less '}
                  {' '}
                  <ArrowRightIcon color={primaryColor} />
                </ReadMore>
              )
              : null}

          </div>
        ) : null}

      <Row gutter={[24, 24]} className="Grid-width-row">
        {!loading ? companies && companies
          .slice(0, !seeAllCompetitors && companies.length > 12 ? companies.length : 12)
          .map((item) => (
            <Col xxl={{ span: 4 }} xl={{ span: 4 }} lg={{ span: 6 }} className="gutter-row">
              <CompanyCardLarge
                companyData={item}
                key={item.companyId}
                showDrawerIcon={!!item.companyNotes}
                showSapIcon={!!item.accountPlanAllowed}
              />
            </Col>
          )) : getCompaniesSkeleton()}
      </Row>
      {!loading ? (
        <div className="mt-c-50 heading-text">
          <h2>
            Top Competitor Executives
          </h2>
          {executives && executives.length > 6
            ? (
              <ReadMore
                onClick={seeMoreLess2}
                onKeyPress={seeMoreLess2}
                role="button"
                tabIndex={0}
                aria-label={seeAllCompetitors ? 'See More ' : 'See Less '}
              >
                {seeAllExecutives ? 'See More ' : 'See Less '}
                {' '}
                <ArrowRightIcon color={primaryColor} />
              </ReadMore>
            )
            : null}

        </div>
      ) : null}

      <Row gutter={[24, 10]} className="mb-90">
        {loading ? getExecutivesSkeleton() : getExecutives()}
      </Row>
    </Wrapper>
  );
};

export default React.memo(Competitors);
